/**
 * Correcting a workspace's details, its feature set and its subscription.
 *
 * THE FORM IS A DIFF, NOT A DOCUMENT. It loads the tenant, tracks what the
 * operator moved, and sends only that — see `tenant-edit.ts` for why, and for
 * the three-state absent/`null`/value rule that lets a website entered by
 * mistake actually be cleared. The consequence visible here is the submit
 * button: it names the number of fields about to change, and it is disabled
 * when that number is zero, so "Save" never means "I have no idea whether this
 * will do anything".
 *
 * EVERY SAVE STATES WHAT IT WILL DO BEFORE IT DOES IT. The panel above the
 * buttons lists each field that will move, old value to new. This is a staff
 * member acting on somebody else's workspace, from a connection that is
 * `BYPASSRLS` — the one role that can see and write every tenant — and the cost
 * of a mis-click here is borne by a customer who cannot see it happen. A summary
 * that has to be read before the button can be pressed is the cheapest control
 * available, and unlike a confirmation dialog it does not train people to click
 * through it.
 *
 * WHY THE UN-EDITABLE FIELDS ARE ON SCREEN AND NOT HIDDEN. A form that simply
 * omits the subscription key reads as a form that forgot it. Staff then go
 * looking, and what they find is a database console. So the key, the workspace
 * id and the timestamps are rendered — as facts, in a panel that says in one
 * sentence each why they are not editable and what the operation would be
 * instead. `.strict()` on `tenantEditableFieldsSchema` is what actually enforces
 * that; this panel is what stops it being experienced as a bug.
 *
 * THE EMPTY RESULT IS A REAL ANSWER. `updateTenantResponse.changes` is computed
 * against the stored row, so it can come back empty even for a request that
 * named three fields — two operators editing the same tenant, or a value that
 * matched after trimming. That case is reported in words by the parent screen,
 * not as a success toast saying "Saved", because a success message for a save
 * that moved nothing teaches people the button works when it did not.
 *
 * THE BLUEPRINT LOOK (slice 66), visual only. Features are chosen with the
 * shared `FeaturePicker` — toggle chips with each part's description kept
 * beneath them — instead of a checkbox grid written out twice, once here and
 * once on `/tenants/new`. The pending-changes panel is `@wend/ui`'s
 * `MappingList`: one row per field, the old value on the left, the new one on
 * the right, joined by an arrow a screen reader hears as "becomes". It is the
 * same before-and-after the panel always showed; what changed is that each
 * field is now its own bordered row, so a long list of edits reads as a list
 * of edits rather than a paragraph.
 */
import { Undo2 } from 'lucide-react';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import {
  MAX_ADMIN_REASON_LENGTH,
  tenantEditFieldPaths,
  updateTenantRequestSchema,
  type Feature,
  type Tenant,
  type TenantEditFieldPath,
  type UpdateTenantResponse,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  FormField,
  Input,
  Kicker,
  Label,
  MappingList,
  MappingRow,
} from '@wend/ui';

import { FeaturePicker } from '../../components/FeaturePicker';
import { FormAlert } from '../../components/FormAlert';
import { describeApiError, toApiError, type ApiError } from '../../lib/api-error';
import { todayInputValue } from '../../lib/date-input';
import { FEATURE_COPY, inPillarOrder } from '../../lib/features';
import { formatDateTime } from '../../lib/format';
import {
  NO_FIELD_ERRORS,
  toFieldErrorMap,
  zodFieldErrors,
  type FieldErrorMap,
} from '../../lib/form';
import { TENANT_FIELD_LABEL } from '../../lib/tenant-fields';
import { updateTenant } from '../../lib/tenants-api';
import { ReasonField } from './ReasonField';
import {
  buildChanges,
  editErrorKey,
  formStateFromTenant,
  type TenantEditFormState,
} from './tenant-edit';

/**
 * Where zod would say "Invalid input" — `isoDateTimeSchema` is a union and
 * cannot describe itself usefully — say something a person can act on. Only
 * applied to zod's own generic messages; see `toFieldErrorMap`.
 */
const FRIENDLY_MESSAGES: Readonly<Record<string, string>> = {
  'changes.validTill': 'Choose the date the subscription expires.',
  'changes.features': 'Enable at least one feature.',
  reason: 'Say why you are making this change.',
};

export interface TenantEditFormProps {
  tenant: Tenant;
  /** Called with the API's own answer — including an empty `changes`. */
  onSaved: (result: UpdateTenantResponse) => void;
}

export function TenantEditForm({ tenant, onSaved }: TenantEditFormProps) {
  const initial = useMemo(() => formStateFromTenant(tenant), [tenant]);

  const [form, setForm] = useState<TenantEditFormState>(initial);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<FieldErrorMap>(NO_FIELD_ERRORS);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [localMessage, setLocalMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const changes = useMemo(
    () => buildChanges(tenant, initial, form),
    [tenant, initial, form],
  );
  const pendingPaths = useMemo(() => tenantEditFieldPaths(changes), [changes]);
  const isDirty = pendingPaths.length > 0;

  const update = (patch: Partial<TenantEditFormState>): void => {
    setForm((current) => ({ ...current, ...patch }));
  };

  // `FeaturePicker` hands back the set rebuilt in display order. The set is
  // what matters: `renderFeatureSet` sorts it before the dirty check and before
  // the audit trail records it, so order never reads as a change.
  const setFeatures = (features: Feature[]): void => {
    setForm((current) => ({ ...current, features }));
  };

  const discard = (): void => {
    setForm(initial);
    setReason('');
    setErrors(NO_FIELD_ERRORS);
    setFailure(null);
    setLocalMessage(null);
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    setLocalMessage(null);

    // Clearing the date input is the one edit the diff cannot represent.
    // `validTill` is NOT NULL, so `buildChanges` deliberately omits an empty
    // value rather than sending a `null` the contract has no member for — which
    // means an emptied date looks, to everything downstream, exactly like an
    // untouched one. Without this the operator clears the field, presses Save,
    // and is told nothing has changed, which is true of the request and not of
    // what they see on the screen.
    if (form.validTill === '') {
      setErrors({
        [editErrorKey('validTill')]:
          'A subscription must expire on some date — there is no “no expiry”. Choose one, or press Discard to put the current one back.',
      });
      setLocalMessage('Some details need attention. Check the highlighted fields.');
      return;
    }

    if (!isDirty) {
      // Caught here rather than left to the contract's own refusal, which is
      // phrased for an API client ("Name at least one field to change") and
      // would arrive as a red form error for something that is not an error.
      setLocalMessage('Nothing has changed yet. Edit a field before saving.');
      return;
    }

    const parsed = updateTenantRequestSchema.safeParse({ changes, reason });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error, FRIENDLY_MESSAGES));
      setLocalMessage('Some details need attention. Check the highlighted fields.');
      return;
    }

    setErrors(NO_FIELD_ERRORS);
    setSaving(true);

    try {
      onSaved(await updateTenant(tenant.id, parsed.data));
    } catch (error: unknown) {
      const apiError = toApiError(error);
      setFailure(apiError);
      setErrors(toFieldErrorMap(apiError.fieldErrors, FRIENDLY_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit();
  };

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      {failure !== null && (
        <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>
      )}
      {failure === null && localMessage !== null && <FormAlert>{localMessage}</FormAlert>}

      <Section
        title="Workspace"
        description="The name people see inside the product, and the address Wend contacts about the account. Not a login — a workspace's users are managed by its own owner."
      >
        <TextField
          label={TENANT_FIELD_LABEL.name}
          required
          value={form.name}
          error={errors[editErrorKey('name')]}
          onChange={(value) => {
            update({ name: value });
          }}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label={TENANT_FIELD_LABEL.email}
            required
            type="email"
            description="Billing and operations."
            value={form.email}
            error={errors[editErrorKey('email')]}
            onChange={(value) => {
              update({ email: value });
            }}
          />
          <TextField
            label={TENANT_FIELD_LABEL.phone}
            required
            type="tel"
            description="E.164, e.g. +919812345678"
            value={form.phone}
            error={errors[editErrorKey('phone')]}
            onChange={(value) => {
              update({ phone: value });
            }}
          />
        </div>
      </Section>

      <Section
        title="Organisation"
        description="The legal entity behind the workspace. Every field here may be left blank — clearing one that has a value removes it."
      >
        <TextField
          label={TENANT_FIELD_LABEL['organisation.legalName']}
          value={form.legalName}
          error={errors[editErrorKey('organisation.legalName')]}
          onChange={(value) => {
            update({ legalName: value });
          }}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label={TENANT_FIELD_LABEL['organisation.website']}
            type="url"
            value={form.website}
            error={errors[editErrorKey('organisation.website')]}
            onChange={(value) => {
              update({ website: value });
            }}
          />
          <TextField
            label={TENANT_FIELD_LABEL['organisation.taxId']}
            description="GSTIN, VAT or equivalent."
            value={form.taxId}
            error={errors[editErrorKey('organisation.taxId')]}
            onChange={(value) => {
              update({ taxId: value });
            }}
          />
        </div>

        <TextField
          label={TENANT_FIELD_LABEL['organisation.addressLine1']}
          value={form.addressLine1}
          error={errors[editErrorKey('organisation.addressLine1')]}
          onChange={(value) => {
            update({ addressLine1: value });
          }}
        />
        <TextField
          label={TENANT_FIELD_LABEL['organisation.addressLine2']}
          value={form.addressLine2}
          error={errors[editErrorKey('organisation.addressLine2')]}
          onChange={(value) => {
            update({ addressLine2: value });
          }}
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            label={TENANT_FIELD_LABEL['organisation.city']}
            value={form.city}
            error={errors[editErrorKey('organisation.city')]}
            onChange={(value) => {
              update({ city: value });
            }}
          />
          <TextField
            label={TENANT_FIELD_LABEL['organisation.state']}
            value={form.state}
            error={errors[editErrorKey('organisation.state')]}
            onChange={(value) => {
              update({ state: value });
            }}
          />
          <TextField
            label={TENANT_FIELD_LABEL['organisation.postalCode']}
            value={form.postalCode}
            error={errors[editErrorKey('organisation.postalCode')]}
            onChange={(value) => {
              update({ postalCode: value });
            }}
          />
          <TextField
            label={TENANT_FIELD_LABEL['organisation.country']}
            description="Two letters, e.g. IN"
            value={form.country}
            error={errors[editErrorKey('organisation.country')]}
            onChange={(value) => {
              update({ country: value });
            }}
          />
        </div>
      </Section>

      <Section
        title="Subscription and access"
        description="Sign-in stops the moment either of these says it should — an expired subscription and a suspended workspace both lock everyone out, and neither deletes anything."
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label={TENANT_FIELD_LABEL.validTill}
            required
            type="date"
            min={todayInputValue()}
            description={`Runs to the end of this day in your timezone. Currently ${formatDateTime(
              tenant.validTill,
            )}.`}
            value={form.validTill}
            error={errors[editErrorKey('validTill')]}
            onChange={(value) => {
              update({ validTill: value });
            }}
          />

          <div className="flex flex-col gap-2">
            <Label asChild>
              <span>{TENANT_FIELD_LABEL.isActive}</span>
            </Label>
            <Checkbox
              checked={form.isActive}
              label="This workspace may sign in"
              description="Clearing this suspends every user in the workspace immediately. Their data, campaigns and credits are untouched, and re-ticking it restores access."
              onCheckedChange={(checked) => {
                update({ isActive: checked });
              }}
            />
          </div>
        </div>

        <FeaturePicker
          legend={TENANT_FIELD_LABEL.features}
          description="What this workspace can see once its users sign in. Saving replaces the whole set — turning one off turns that product off for everyone in the workspace. At least one must stay on."
          value={form.features}
          onChange={setFeatures}
          error={errors[editErrorKey('features')]}
        />
      </Section>

      <Section
        title="Save"
        description="Nothing above has been sent yet. This panel is what will be written, and to whose account it will be attributed."
      >
        <PendingChanges initial={initial} form={form} paths={pendingPaths} />

        {/*
          `MAX_ADMIN_REASON_LENGTH`, because this reason is stored in
          `admin_audit.reason` and 0015's `admin_audit_reason_is_an_explanation`
          takes 500. The top-up form on the same screen passes a lower cap for a
          different column — see `ReasonField`'s header.
        */}
        <ReasonField
          value={reason}
          onChange={setReason}
          error={errors.reason}
          maxLength={MAX_ADMIN_REASON_LENGTH}
          explains="somebody reading this workspace's history needs to know what prompted the correction — a support ticket number, an email from the customer, the mistake being fixed."
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" loading={saving} disabled={!isDirty}>
            {isDirty
              ? `Save ${String(pendingPaths.length)} ${pendingPaths.length === 1 ? 'change' : 'changes'}`
              : 'Save'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            disabled={!isDirty && reason === ''}
            leadingIcon={<Undo2 aria-hidden="true" />}
            onClick={discard}
          >
            Discard
          </Button>
        </div>
      </Section>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* What this save will do                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The old value and the new one, per field, before anything is sent.
 *
 * It reads the *form* for both sides rather than the diff object, because the
 * diff carries `null` for a cleared field and `null` is not a sentence. What is
 * wanted here is what the operator will see change on the screen they are
 * looking at.
 */
function PendingChanges({
  initial,
  form,
  paths,
}: {
  /** The tenant as it was loaded, in form terms. The "before" column. */
  initial: TenantEditFormState;
  form: TenantEditFormState;
  paths: readonly TenantEditFieldPath[];
}) {
  if (paths.length === 0) {
    return (
      <p className="text-[0.9rem] leading-relaxed text-muted">
        No field has been edited yet, so there is nothing to save. Change something above and this
        panel will list exactly what will be written.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* The count is a sans tabular numeral beside the kicker's words, not
          inside the mono caps: R6, the dotted-zero misread (slice 66 review). */}
      <p className="flex items-center gap-2 after:h-px after:flex-1 after:bg-line">
        <span className="text-[0.8rem] font-medium tabular-nums text-text">{paths.length}</span>
        <Kicker as="span">{paths.length === 1 ? 'field will change' : 'fields will change'}</Kicker>
      </p>

      {/*
        One row per field: its name over the old value (struck through), the
        arrow, the new value. The arrow is hidden from screen readers and the
        row says "becomes" instead, so "Workspace name, Northwind, becomes
        Northwind Retail" is what is heard.
      */}
      <MappingList>
        {paths.map((path) => (
          <MappingRow
            key={path}
            arrowLabel="becomes"
            from={
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[0.76rem] text-muted">{TENANT_FIELD_LABEL[path]}</span>
                <span className="break-words text-muted line-through">{describeFormValue(path, initial)}</span>
              </span>
            }
            to={<span className="break-words text-[0.88rem] font-medium text-text">{describeFormValue(path, form)}</span>}
          />
        ))}
      </MappingList>
    </div>
  );
}

/** One field of the form state, as the sentence the summary shows. */
function describeFormValue(path: TenantEditFieldPath, state: TenantEditFormState): string {
  if (path === 'isActive') {
    return state.isActive ? 'Active — can sign in' : 'Suspended — cannot sign in';
  }
  if (path === 'features') {
    return state.features.length === 0
      ? '(none)'
      : inPillarOrder(state.features)
          .map((feature) => FEATURE_COPY[feature].label)
          .join(', ');
  }
  if (path === 'validTill') {
    return state.validTill === '' ? '(not set)' : state.validTill;
  }

  const key = path.startsWith('organisation.') ? path.slice('organisation.'.length) : path;
  const value = state[key as keyof TenantEditFormState];
  if (typeof value !== 'string') return String(value);
  return value.trim() === '' ? '(empty)' : value;
}

/* -------------------------------------------------------------------------- */
/* Local pieces — the same ones NewTenantPage uses                             */
/* -------------------------------------------------------------------------- */

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  description?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'tel' | 'url' | 'date';
  autoComplete?: string;
  min?: string;
}

function TextField({
  label,
  value,
  onChange,
  error,
  description,
  required = false,
  type = 'text',
  autoComplete,
  min,
}: TextFieldProps) {
  return (
    <FormField label={label} error={error} description={description} required={required}>
      <Input
        type={type}
        value={value}
        autoComplete={autoComplete}
        min={min}
        spellCheck={type === 'email' || type === 'url' ? false : undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </FormField>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1.02rem]">{title}</CardTitle>
        {description !== undefined && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">{children}</CardContent>
    </Card>
  );
}
